import React, { useState, useEffect } from 'react';
import { UserProfile } from '../../types';
import { db, isUsingMockKeys } from '../../firebase';
import { doc, onSnapshot, query, collection, where, getDocs } from 'firebase/firestore';
import { useWBL } from '../../context/WBLContext';

export interface StudentAvatarProps {
  studentId?: string;
  studentName?: string;
  studentEmail?: string;
  profile?: UserProfile;
  imageUrl?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

export const isUrl = (url: any): boolean => {
  if (!url || typeof url !== 'string') return false;
  const s = url.trim();
  return s.length > 5 && (
    s.startsWith('http://') || 
    s.startsWith('https://') || 
    s.startsWith('/') || 
    s.startsWith('data:') || 
    s.startsWith('gs://')
  );
};

export const resolveGsUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  const s = url.trim();
  if (!s.startsWith('gs://')) return s;
  try {
    const parts = s.slice(5).split('/');
    const bucket = parts[0];
    const objectPath = parts.slice(1).join('/');
    const encodedPath = encodeURIComponent(objectPath);
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
  } catch (e) {
    console.warn("Failed to parse gs:// URL:", s, e);
    return s;
  }
};

const getInitials = (name?: string, email?: string): string => {
  let displayName = name || '';
  if ((!displayName || displayName === 'Unnamed' || displayName === 'Student' || displayName === 'User') && email) {
    displayName = email.split('@')[0];
  }
  const clean = displayName.replace(/[^a-zA-Z0-9\s,]/g, '').trim();
  if (!clean) return 'US';

  // Handle "Lastname, Firstname" format if present
  if (clean.includes(',')) {
    const parts = clean.split(',').map(s => s.trim()).filter(Boolean);
    if (parts.length >= 2) {
      const last = parts[0][0] || '';
      const first = parts[1][0] || '';
      return `${first}${last}`.toUpperCase() || 'US';
    }
  }

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 0) return 'US';
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

const getPalette = (seedStr: string) => {
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = seedStr.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % 6;

  const palettes = [
    {
      // Dark Blue-Gray / Steel Blue
      bg: 'from-[#0f2d42] via-[#163a52] to-[#1e4860]',
      border: 'border-cyan-500/20',
    },
    {
      // Muted Teal / Deep Slate
      bg: 'from-[#0d3338] via-[#13444a] to-[#1c5860]',
      border: 'border-teal-500/20',
    },
    {
      // Navy / Slate Blue
      bg: 'from-[#0f172a] via-[#1e293b] to-[#2d3d54]',
      border: 'border-sky-500/20',
    },
    {
      // Charcoal & Steel
      bg: 'from-[#18222d] via-[#22303e] to-[#2c3e50]',
      border: 'border-slate-400/20',
    },
    {
      // Deep Indigo / Steel Navy
      bg: 'from-[#131b2e] via-[#1c2942] to-[#263756]',
      border: 'border-blue-500/20',
    },
    {
      // Dark Cyan Teal
      bg: 'from-[#0a2e36] via-[#123e48] to-[#1a4f5b]',
      border: 'border-emerald-500/20',
    },
  ];

  return palettes[index];
};

export const StudentAvatar: React.FC<StudentAvatarProps> = ({ 
  studentId, 
  studentName, 
  studentEmail, 
  profile: passedProfile,
  imageUrl: passedImageUrl,
  className = "w-full h-full",
  size = 'md'
}) => {
  const { users } = useWBL();
  const [localUser, setLocalUser] = useState<UserProfile | null>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const checkPhoto = (p?: UserProfile) => {
      const url = [
        p?.avatar, 
        p?.photoURL, 
        (p as any)?.profileImage, 
        (p as any)?.photoUrl, 
        (p as any)?.logoUrl,
        (p as any)?.picture,
        (p as any)?.photo,
        (p as any)?.profilePic
      ].find(isUrl);
      return !!url;
    };

    if (passedImageUrl && isUrl(passedImageUrl)) return;
    if (passedProfile && checkPhoto(passedProfile)) return;
    if (!studentId || isUsingMockKeys) return;
    
    const contextUser = (users || []).find(u => u && u.uid === studentId);
    if (checkPhoto(contextUser)) return;

    // Fetch by ID and Email in parallel for maximum reliability
    const unsub = onSnapshot(doc(db, 'users', studentId), (snap) => {
      if (snap.exists()) {
        const data = snap.data() as UserProfile;
        setLocalUser(data);
      }
    }, (err) => {
      console.warn("Avatar ID fetch restriction:", err.message);
    });

    if (studentEmail) {
      const qEmail = query(collection(db, 'users'), where('email', '==', studentEmail));
      getDocs(qEmail).then(qSnap => {
        if (!qSnap.empty) {
          const data = qSnap.docs[0].data() as UserProfile;
          setLocalUser(data);
        }
      }).catch(err => console.warn("Email fallback fetch failed:", err));
    }
    
    return () => unsub();
  }, [studentId, users, passedProfile, studentEmail, passedImageUrl]);

  const profile = passedProfile || (studentId ? (users || []).find(u => u && u.uid === studentId) : null) || localUser;

  const rawImgUrl = passedImageUrl && isUrl(passedImageUrl) 
    ? passedImageUrl 
    : [
        profile?.avatar, 
        profile?.photoURL, 
        (profile as any)?.profileImage, 
        (profile as any)?.photoUrl,
        (profile as any)?.logoUrl,
        (profile as any)?.picture,
        (profile as any)?.photo,
        (profile as any)?.profilePic
      ].find(isUrl);

  const finalImgUrl = resolveGsUrl(rawImgUrl);

  // Reset imgError if the url changes
  useEffect(() => {
    setImgError(false);
  }, [finalImgUrl]);

  if (finalImgUrl && !imgError) {
    return (
      <img 
        src={finalImgUrl} 
        alt={studentName || 'Avatar'} 
        className={`${className} ${className.includes('object-') ? '' : 'object-cover'}`} 
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
      />
    );
  }

  const name = studentName || profile?.name || '';
  const email = studentEmail || profile?.email || '';
  const initials = getInitials(name, email);
  const seed = studentId || name || email || 'user';
  const palette = getPalette(seed);

  return (
    <div 
      className={`w-full h-full bg-gradient-to-br ${palette.bg} ${palette.border} border flex items-center justify-center relative overflow-hidden select-none shadow-xs ${className}`}
    >
      {/* Translucent diagonal glass pane sheen overlay */}
      <div 
        className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.08] to-white/[0.22] pointer-events-none" 
        style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 0)' }}
      />
      {/* Secondary subtle inner highlight */}
      <div className="absolute inset-0 border border-white/10 pointer-events-none" />

      {/* Centered Semibold White Monogram Initials */}
      <span className="font-semibold tracking-wider text-white drop-shadow-xs z-10 uppercase font-sans text-xs sm:text-sm">
        {initials}
      </span>
    </div>
  );
};

